import React, { ChangeEvent, FocusEvent, ClipboardEvent, ElementType } from 'react';
import styles from './TextArea.module.scss';
import { INPUT_ICON_POS, VALIDATION_ERROR } from '@types';
import { Spacer, Caption } from '@components';
import { ZodTypeAny } from 'zod';

interface InputFieldProps extends React.ComponentPropsWithoutRef<'textarea'> {
	value: string;
	onChange: (e:ChangeEvent<HTMLTextAreaElement>) => void;
	disabled?: boolean;
	Icon?: ElementType;
	iconPos?: INPUT_ICON_POS;
	label?: string;
	charCount?: boolean;
	charCountLimit?: number;
	onFocus?: (e:FocusEvent<HTMLTextAreaElement>) => void;
	onBlur?: (e:FocusEvent<HTMLTextAreaElement>) => void;
	placeholder?: string;
	required?: boolean;
	inputRef?: any;
	stretch?: boolean;
	name: string;
	textAreaHeight?: string;
	validations?: ZodTypeAny;
	hasError?: boolean;
	errorMessage?: string | JSX.Element;
	onValidationError?: (error:VALIDATION_ERROR, clear:boolean) => void;
	onValidationSuccess?: (success:VALIDATION_ERROR, clear: boolean) => void;
	keepErrorSpace?: boolean;
	onPaste?: (e:ClipboardEvent<HTMLTextAreaElement>) => void;
}

export const TextArea = (props:InputFieldProps) => {
	const {
		value,
		onChange,
		disabled = false,
		Icon = null,
		iconPos = INPUT_ICON_POS.RIGHT,
		label,
		inputRef,
		charCount = false,
		charCountLimit = 524288, // This is the legit default and max length a HTML input field can have...
		onFocus,
		onBlur,
		placeholder,
		required = false,
		stretch = false,
		textAreaHeight = '200px',
		name,
		validations,
		hasError,
		errorMessage,
		onValidationError,
		onValidationSuccess,
		keepErrorSpace = true,
		onPaste,
		...otherProps
	} = props;
	const onFieldFocus = (e:FocusEvent<HTMLTextAreaElement>) => {
		if (onFocus) onFocus(e);
	}
	const onFieldBlur = (e:FocusEvent<HTMLTextAreaElement>) => {
		if (onBlur) onBlur(e);
		if (validations) {
			const valid = validations.safeParse(value);
			if (!valid.success) {
				const issues = [];
				for (let i=0, l=valid.error.issues.length; i<l; ++i) {
					issues.push(valid.error.issues[i].message);
				}
				const error = {
					field: name,
					issues
				};
				if (onValidationError) {
					onValidationError(error, false);
				}
			} else if (onValidationSuccess) {
				onValidationSuccess({ field: name }, true);
			}
		}
	}

	return (
		<div data-testid={'@Ofload-InputContainer'} className={`${styles.column} ${stretch ? styles.flex : ''}`}>
			{(label || charCount) && <div className={styles.labelsContainer}>
				{label && <label className={styles.label}>{label}{required ? <span className={styles.required}> *</span> : <></>}</label>}
				{charCount && <p className={styles.charCount}>{charCountLimit > 0 ? `${value.length}/${charCountLimit}` : value.length}</p>}
			</div>}
			<div data-testid={'@Ofload-TextAreaInnerContainer'} className={`${styles.container} ${styles.alignTop} ${disabled ? styles.disabled : ''} ${hasError ? styles.error : ''}`}>
				{Icon !== null && iconPos === INPUT_ICON_POS.LEFT && <Icon data-testid={'@Ofload-InputIcon-Left'} className={`${styles.icon} ${styles.iconLeft} ${disabled ? styles.disabledIcon : undefined}`} />}
				<textarea data-testid={'@Ofload-TextArea'} disabled={disabled} onChange={onChange} maxLength={charCountLimit} onFocus={onFieldFocus} onBlur={onFieldBlur} placeholder={placeholder} value={value || ''} name={name} style={{ height: textAreaHeight }} onPaste={onPaste} {...otherProps} />
				{Icon !== null && iconPos === INPUT_ICON_POS.RIGHT && <Icon data-testid={'@Ofload-InputIcon-Right'} className={`${styles.icon} ${styles.iconRight} ${disabled ? styles.disabledIcon : undefined}`} />}
			</div>
			{hasError && <div className={styles.errorMsgContainer}>
				<Spacer size={5} />
				<Caption color={'var(--txt-error)'}>
					{errorMessage}
				</Caption>
			</div>}
			{keepErrorSpace && !hasError && <div className={styles.errorMsgContainer} />}
		</div>
	);
}
